import { useState, useEffect, useCallback } from 'react'
import type { CartItem, Product } from '../types'
import * as db from '../services/db'

export function useCart() {
  const [cart, setCart] = useState<CartItem[]>([])
  const [products, setProducts] = useState<Map<string, Product>>(new Map())

  const load = useCallback(async () => {
    const [cartItems, allProducts] = await Promise.all([db.getCart(), db.getAllProducts()])
    setCart(cartItems)
    setProducts(new Map(allProducts.map((p) => [p.id, p])))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const addByQr = useCallback(
    async (qrValue: string) => {
      const product = await db.getProductByQr(qrValue)
      if (!product) return false
      await db.addToCart(product.id, 1, product.price)
      await load()
      return true
    },
    [load]
  )

  const addByProductIdWithQuantity = useCallback(
    async (productId: string, quantity: number) => {
      if (quantity < 1) return false
      const product = await db.getProductById(productId)
      if (!product) return false
      await db.addToCart(product.id, quantity, product.price * quantity)
      await load()
      return true
    },
    [load]
  )

  const addByProductId = useCallback(
    async (productId: string) => {
      return addByProductIdWithQuantity(productId, 1)
    },
    [addByProductIdWithQuantity]
  )

  const removeItem = useCallback(
    async (productId: string) => {
      await db.removeFromCart(productId)
      await load()
    },
    [load]
  )

  const clear = useCallback(async () => {
    await db.clearCart()
    await load()
  }, [load])

  const subtotal = cart.reduce((sum, c) => sum + c.subtotal, 0)
  const itemCount = cart.reduce((sum, c) => sum + c.quantity, 0)

  return {
    cart,
    products,
    subtotal,
    itemCount,
    addByQr,
    addByProductId,
    addByProductIdWithQuantity,
    removeItem,
    clear,
    refresh: load,
  }
}
